import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from '../../axios';
import config from '../../config';
import './VerifyLead.css';

const VerifyLead = () => {
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);
  
  // Parse URL parameters
  const params = new URLSearchParams(location.search);
  const leadId = params.get('lead_id') || '';
  const token = params.get('token') || '';
  const address = params.get('address') || '';
  const phone = params.get('phone') || '';
  const email = params.get('email') || '';
  const propertyType = params.get('propertyType') || '';
  const numRooms = params.get('numRooms') || '';
  const area = params.get('area') || '';
  const ownership = params.get('ownership') || '';
  const estimatedValue = params.get('estimatedValue') || '';

  const handleVerification = async (isCorrect) => {
    if (!leadId || !token) {
      setError('Saknade parametrar för verifiering.');
      return;
    }

    setLoading(true);
    setError(null);
    setFeedback(null);

    try {
      const response = await axios.post(`${config.backend_url}/api/leads/verify-decision`, {
        lead_id: leadId,
        token: token,
        is_correct: isCorrect
      });

      if (response.data.success) {
        setFeedback({
          type: 'success',
          message: response.data.message,
          details: response.data.details || {}
        });
      } else {
        setError(response.data.error || 'Ett fel uppstod vid verifiering.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.error || 'Ett fel uppstod vid verifiering. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.push('/');
  };

  if (feedback) {
    return (
      <div className="verify-lead-container">
        <div className="verify-lead-feedback">
          <div className={`feedback-box ${feedback.type}`}>
            <FontAwesomeIcon 
              icon={feedback.type === 'success' ? faCheckCircle : faTimesCircle} 
              className="feedback-icon"
            />
            <h2>{feedback.type === 'success' ? 'Verifiering Genomförd' : 'Verifiering Misslyckades'}</h2>
            <p>{feedback.message}</p>
            
            {feedback.details.google_ads_sent && (
              <div className="feedback-details">
                <p><strong>✓ Google Ads Conversion:</strong> Skickad med värde {feedback.details.conversion_value} SEK</p>
                {feedback.details.gclid && <p><strong>GCLID:</strong> {feedback.details.gclid}</p>}
                {feedback.details.wbraid && <p><strong>WBRAID:</strong> {feedback.details.wbraid}</p>}
                {feedback.details.gbraid && <p><strong>GBRAID:</strong> {feedback.details.gbraid}</p>}
              </div>
            )}
            
            {feedback.details.conversion_value === 0 && (
              <div className="feedback-details">
                <p><strong>ℹ️ Lead markerat som inkorrekt:</strong> Ingen konvertering skickad till Google Ads</p>
              </div>
            )}
            
            <button className="back-button-feedback" onClick={handleGoBack}>
              <FontAwesomeIcon icon={faArrowLeft} /> Tillbaka till startsidan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-lead-container">
      <div className="verify-lead-content">
        <div className="verify-lead-header">
          <h1>Bodex Lead Verification</h1>
          <h2>Är detta lead korrekt?</h2>
        </div>

        <div className="verify-lead-info">
          <h3>Lead Information</h3>
          <table className="verify-lead-table">
            <tbody>
              <tr>
                <td><strong>📍 Adress:</strong></td>
                <td>{address}</td>
              </tr>
              <tr>
                <td><strong>📱 Telefon:</strong></td>
                <td>{phone}</td>
              </tr>
              <tr>
                <td><strong>✉️ E-post:</strong></td>
                <td>{email}</td>
              </tr>
              {propertyType && (
                <tr>
                  <td><strong>🏠 Typ:</strong></td>
                  <td>{propertyType}</td>
                </tr>
              )}
              {numRooms && (
                <tr>
                  <td><strong>🛏️ Antal rum:</strong></td>
                  <td>{numRooms}</td>
                </tr>
              )}
              {area && (
                <tr>
                  <td><strong>📐 Boarea:</strong></td>
                  <td>{area} m²</td>
                </tr>
              )}
              {ownership && (
                <tr>
                  <td><strong>🔑 Ägarskap:</strong></td>
                  <td>{ownership}</td>
                </tr>
              )}
              {estimatedValue && (
                <tr>
                  <td><strong>💰 Värdering:</strong></td>
                  <td>{estimatedValue}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {error && (
          <div className="verify-lead-error">
            <FontAwesomeIcon icon={faTimesCircle} /> {error}
          </div>
        )}

        <div className="verify-lead-actions">
          <button 
            className="verify-button correct"
            onClick={() => handleVerification(true)}
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Verifierar...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} /> Ja - Korrekt Lead
              </>
            )}
          </button>
          
          <button 
            className="verify-button incorrect"
            onClick={() => handleVerification(false)}
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Verifierar...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTimesCircle} /> Nej - Inkorrekt Lead
              </>
            )}
          </button>
        </div>

        <div className="verify-lead-note">
          <p><strong>Obs:</strong> Om du markerar leadet som korrekt kommer en offline-konvertering att skickas till Google Ads med rätt konversionsvärde baserat på ägarskap och säljtidsintention.</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyLead;
